import React from 'react'
import { Link } from 'react-router-dom'
import {Moon, Clock, MapPin} from 'lucide-react'

function Nightlife() {
  const spots = [
    {
      id: 1,
      name: 'Golden Gai',
      area: 'Shinjuku',
      description: 'Legendary alleyway of 200+ tiny bars, each with unique character. The heart of Tokyo\'s drinking culture.',
      image: 'https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=800&q=80',
      vibe: 'Intimate & Eclectic',
      avgPrice: '¥2,000-4,000',
      bestTime: '9pm - 2am',
      link: '/article/golden-gai'
    },
    {
      id: 2,
      name: 'Roppongi Clubs',
      area: 'Roppongi',
      description: 'International party district with mega-clubs, rooftop bars, and all-night energy.',
      image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&q=80',
      vibe: 'High Energy',
      avgPrice: '¥3,000-6,000',
      bestTime: '11pm - 5am',
      link: '/article/roppongi-clubs'
    },
    {
      id: 3,
      name: 'Shibuya Clubbing',
      area: 'Shibuya',
      description: 'Electronic music mecca with underground techno clubs and famous DJ sets.',
      image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800&q=80',
      vibe: 'Underground',
      avgPrice: '¥2,500-5,000',
      bestTime: '12am - 6am',
      link: '/article/shibuya-clubs'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#1A0A1A] to-[#0A0A0A]">
      {/* Header with Neon */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551624107-feebf41d5ca3?w=1600&q=80')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <Moon className="w-10 h-10 text-[#FF006E]" fill="#FF006E" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Tokyo <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF006E] to-[#00E5FF]">Nightlife</span>
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            From intimate micro-bars to massive mega-clubs, Tokyo's nightlife scene is unmatched. 
            Here's where locals and international crowds collide after dark.
          </p>
        </div>
      </section>

      {/* Featured Spots */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="space-y-8">
          {spots.map((spot, index) => (
            <div
              key={spot.id}
              className="group relative bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-3xl overflow-hidden border-2 border-[#FF006E]/20 hover:border-[#FF006E] transition-all"
            >
              <div className="flex flex-col lg:flex-row">
                {/* Image */}
                <div className="lg:w-1/2 relative overflow-hidden h-96">
                  <img
                    src={spot.image}
                    alt={spot.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent lg:from-transparent" />
                  
                  {/* Area Badge */}
                  <div className="absolute top-6 left-6">
                    <div className="flex items-center gap-2 bg-[#FF006E]/90 backdrop-blur-sm px-4 py-2 rounded-full">
                      <MapPin className="w-4 h-4 text-white" />
                      <span className="text-white font-bold">{spot.area}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                  <h2 className="text-4xl font-bold text-white mb-3">
                    {spot.name}
                  </h2>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-4 py-1 bg-[#FF006E]/20 text-[#FF006E] rounded-full text-sm font-bold border border-[#FF006E]/30">
                      {spot.vibe}
                    </span>
                  </div>

                  <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                    {spot.description}
                  </p>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-[#1A1A1A] p-4 rounded-xl border border-[#FF006E]/20">
                      <div className="text-gray-400 text-sm mb-1">Average Spend</div>
                      <div className="text-white font-bold">{spot.avgPrice}</div>
                    </div>
                    <div className="bg-[#1A1A1A] p-4 rounded-xl border border-[#00E5FF]/20">
                      <div className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Best Time
                      </div>
                      <div className="text-white font-bold">{spot.bestTime}</div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={spot.link}
                    className="inline-block px-8 py-4 bg-gradient-to-r from-[#FF006E] to-[#00E5FF] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#FF006E]/50 transition-all hover:scale-105 text-center"
                    style={{ boxShadow: '0 0 30px rgba(255, 0, 110, 0.3)' }}
                  >
                    Explore {spot.name} →
                  </Link>
                </div>
              </div>

              {/* Neon Glow Effect */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF006E] via-[#00E5FF] to-[#FF006E] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>

        {/* Safety Notice */}
        <div className="mt-16 bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] p-8 rounded-2xl border-2 border-[#00E5FF]/30">
          <div className="flex items-start gap-4">
            <div className="text-3xl">💡</div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Tokyo Nightlife Tips</h3>
              <ul className="text-gray-400 space-y-2">
                <li>• Last trains run around midnight - plan accordingly or budget for taxis</li>
                <li>• Many bars have small cover charges (¥500-1,000)</li>
                <li>• Cash is still king at most places</li>
                <li>• Golden Gai bars are tiny - groups of 3-4 max</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Nightlife
